import React, { useState } from "react";
import {
  Button,
  Checkbox,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Popover,
  PopoverTrigger,
  PopoverSurface,
  Spinner,
  Text,
  Divider,
  tokens,
} from "@fluentui/react-components";
import { Folder24Regular, Tag24Regular, Delete24Regular } from "@fluentui/react-icons";
import type { CitationSource } from "./Citations";
import type { MailFolder, MasterCategory } from "../mailbox-adapter";

interface EmailListProps {
  emails: CitationSource[];
  disabled: boolean;
  disabledReason?: string;
  onOpen: (id: string) => void;
  onLoadFolders: () => Promise<MailFolder[]>;
  onLoadTags: () => Promise<MasterCategory[]>;
  onMove: (ids: string[], folderName: string) => Promise<void>;
  onApplyTags: (ids: string[], tagNames: string[]) => Promise<void>;
  onDelete: (ids: string[]) => Promise<void>;
}

/**
 * Renders emails surfaced by list_emails/search_emails as a clickable,
 * multi-selectable list — each subject opens the email, and checking one or
 * more rows reveals a bulk action bar (move / tag / delete) that reuses the
 * same adapter calls as the single-email quick actions.
 */
export function EmailList({
  emails,
  disabled,
  disabledReason,
  onOpen,
  onLoadFolders,
  onLoadTags,
  onMove,
  onApplyTags,
  onDelete,
}: EmailListProps) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [folderMenuOpen, setFolderMenuOpen] = useState(false);
  const [folders, setFolders] = useState<MailFolder[]>([]);
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [tagPopoverOpen, setTagPopoverOpen] = useState(false);
  const [tags, setTags] = useState<MasterCategory[]>([]);
  const [tagsLoading, setTagsLoading] = useState(false);
  const [checkedTags, setCheckedTags] = useState<Set<string>>(new Set());
  const [applyingTags, setApplyingTags] = useState(false);
  const [deleting, setDeleting] = useState(false);

  function setChecked(id: string, checked: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }

  async function handleFolderMenuOpenChange(_event: unknown, data: { open: boolean }) {
    setFolderMenuOpen(data.open);
    if (!data.open) {
      return;
    }
    setFoldersLoading(true);
    setFolders([]);
    try {
      setFolders(await onLoadFolders());
    } catch {
      setFolders([]);
    } finally {
      setFoldersLoading(false);
    }
  }

  async function handleTagPopoverOpenChange(_event: unknown, data: { open: boolean }) {
    setTagPopoverOpen(data.open);
    setCheckedTags(new Set());
    if (!data.open) {
      return;
    }
    setTagsLoading(true);
    setTags([]);
    try {
      setTags(await onLoadTags());
    } catch {
      setTags([]);
    } finally {
      setTagsLoading(false);
    }
  }

  function setTagChecked(name: string, checked: boolean) {
    setCheckedTags((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(name);
      } else {
        next.delete(name);
      }
      return next;
    });
  }

  async function handleMove(folderName: string) {
    setFolderMenuOpen(false);
    await onMove(Array.from(selected), folderName);
    setSelected(new Set());
  }

  async function handleApplyTags() {
    if (checkedTags.size === 0) {
      return;
    }
    setApplyingTags(true);
    try {
      await onApplyTags(Array.from(selected), Array.from(checkedTags));
      setTagPopoverOpen(false);
      setCheckedTags(new Set());
      setSelected(new Set());
    } catch {
      // onApplyTags (ChatPanel) already catches its own errors and shows a
      // toast — this only guards against an unhandled rejection.
    } finally {
      setApplyingTags(false);
    }
  }

  async function handleDelete() {
    setDeleting(true);
    try {
      await onDelete(Array.from(selected));
      setSelected(new Set());
    } catch {
      // onDelete (ChatPanel) already catches its own errors and shows a
      // toast — this only guards against an unhandled rejection.
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div
      style={{
        maxWidth: "85%",
        alignSelf: "flex-start",
        padding: "2px 12px",
        display: "flex",
        flexDirection: "column",
        gap: 4,
      }}
    >
      {emails.map((email) => (
        <div key={email.id} style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Checkbox
            checked={selected.has(email.id)}
            onChange={(_e, data) => setChecked(email.id, !!data.checked)}
            aria-label={`Select ${email.subject}`}
          />
          <Button
            appearance="transparent"
            size="small"
            onClick={() => onOpen(email.id)}
            style={{
              height: "auto",
              minHeight: 0,
              padding: 0,
              justifyContent: "flex-start",
              fontSize: tokens.fontSizeBase200,
            }}
          >
            {email.from ? `${email.subject} — ${email.from}` : email.subject}
          </Button>
        </div>
      ))}
      {selected.size > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
          <Text size={100} style={{ color: tokens.colorNeutralForeground3 }}>
            {selected.size} selected
          </Text>
          <Menu open={folderMenuOpen} onOpenChange={(e, data) => void handleFolderMenuOpenChange(e, data)}>
            <MenuTrigger disableButtonEnhancement>
              <Button
                appearance="subtle"
                size="small"
                icon={<Folder24Regular />}
                disabled={disabled}
                title={disabled ? disabledReason : "Move to…"}
              >
                Move to…
              </Button>
            </MenuTrigger>
            <MenuPopover>
              {foldersLoading ? (
                <div style={{ padding: 12 }}>
                  <Spinner size="tiny" label="Loading folders…" />
                </div>
              ) : (
                <MenuList>
                  {folders.length === 0 ? (
                    <MenuItem disabled>No folders found</MenuItem>
                  ) : (
                    folders.map((f) => (
                      <MenuItem key={f.id} onClick={() => void handleMove(f.displayName)}>
                        {f.displayName}
                      </MenuItem>
                    ))
                  )}
                </MenuList>
              )}
            </MenuPopover>
          </Menu>
          <Popover open={tagPopoverOpen} onOpenChange={(e, data) => void handleTagPopoverOpenChange(e, data)}>
            <PopoverTrigger disableButtonEnhancement>
              <Button
                appearance="subtle"
                size="small"
                icon={<Tag24Regular />}
                disabled={disabled}
                title={disabled ? disabledReason : "Apply tags…"}
              >
                Apply tags…
              </Button>
            </PopoverTrigger>
            <PopoverSurface>
              <div style={{ minWidth: 200, display: "flex", flexDirection: "column", gap: 6 }}>
                {tagsLoading ? (
                  <Spinner size="tiny" label="Loading tags…" />
                ) : tags.length === 0 ? (
                  <Text size={200}>No tags found</Text>
                ) : (
                  <>
                    {tags.map((t) => (
                      <Checkbox
                        key={t.id}
                        label={t.displayName}
                        checked={checkedTags.has(t.displayName)}
                        onChange={(_e, data) => setTagChecked(t.displayName, !!data.checked)}
                      />
                    ))}
                    <Divider />
                    <Button
                      appearance="primary"
                      size="small"
                      disabled={checkedTags.size === 0 || applyingTags}
                      onClick={() => void handleApplyTags()}
                    >
                      {applyingTags ? <Spinner size="tiny" /> : `Apply (${checkedTags.size})`}
                    </Button>
                  </>
                )}
              </div>
            </PopoverSurface>
          </Popover>
          <Button
            appearance="subtle"
            size="small"
            icon={deleting ? <Spinner size="tiny" /> : <Delete24Regular />}
            disabled={disabled || deleting}
            title={disabled ? disabledReason : "Delete"}
            onClick={() => void handleDelete()}
          >
            Delete
          </Button>
        </div>
      )}
    </div>
  );
}
